import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { 
  ArrowPathIcon, 
  EyeIcon, 
  CursorArrowRaysIcon, 
  DocumentTextIcon,
  UsersIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const WebsiteAnalytics = () => {
  const [websites, setWebsites] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalWebsites, setTotalWebsites] = useState(0);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('page_views');
  const [sortOrder, setSortOrder] = useState('desc');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data when filters change
  useEffect(() => {
    fetchWebsitesAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, startDate, endDate, debouncedSearchTerm, sortBy, sortOrder]);

  const fetchWebsitesAnalytics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm })
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/analytics/websites-summary?${params}`);
      setWebsites(response.data.data.websites);
      setTotals(response.data.data.totals);
      setTotalPages(response.data.data.pagination.totalPages);
      setTotalWebsites(response.data.data.pagination.total);
    } catch (error) {
      console.error('Error fetching websites analytics:', error);
      toast.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field, order) => {
    // Map table column keys to API sort fields
    const fieldMap = {
      'website_name': 'website_name',
      'page_views': 'page_views',
      'clicks': 'clicks',
      'form_submissions': 'form_submissions',
      'unique_visitors': 'unique_visitors',
      'total_events': 'total_events',
      'last_event': 'last_event'
    };
    
    const sortField = fieldMap[field] || 'page_views';
    setSortBy(sortField);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const formatNumber = (num) => {
    return parseInt(num || 0).toLocaleString();
  };

  const columns = [
    {
      key: 'website_name',
      label: 'Website',
      render: (row) => (
        <div>
          <Link 
            to={`/websites/${row.website_id}`}
            className="font-medium text-primary-600 hover:text-primary-900"
          >
            {row.website_name || 'Unnamed'}
          </Link>
          <p className="text-xs text-gray-500 mt-1">{row.industry || 'N/A'}</p>
          <p className="text-xs text-gray-400">{row.user_email}</p>
        </div>
      )
    },
    {
      key: 'page_views',
      label: 'Page Views',
      render: (row) => (
        <div className="flex items-center gap-2">
          <EyeIcon className="h-4 w-4 text-blue-500" />
          <span className="font-semibold text-gray-900">{formatNumber(row.page_views)}</span>
        </div>
      )
    },
    {
      key: 'clicks',
      label: 'Clicks',
      render: (row) => (
        <div className="flex items-center gap-2">
          <CursorArrowRaysIcon className="h-4 w-4 text-green-500" />
          <span className="font-semibold text-gray-900">{formatNumber(row.clicks)}</span>
        </div>
      )
    },
    {
      key: 'form_submissions',
      label: 'Form Submissions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="h-4 w-4 text-purple-500" />
          <span className="font-semibold text-gray-900">{formatNumber(row.form_submissions)}</span>
        </div>
      )
    },
    {
      key: 'unique_visitors',
      label: 'Unique Visitors',
      render: (row) => (
        <div className="flex items-center gap-2">
          <UsersIcon className="h-4 w-4 text-indigo-500" />
          <span className="font-semibold text-gray-900">{formatNumber(row.unique_visitors)}</span>
        </div>
      )
    },
    {
      key: 'total_events',
      label: 'Total Events',
      render: (row) => (
        <span className="text-gray-700">{formatNumber(row.total_events)}</span>
      )
    },
    {
      key: 'last_event',
      label: 'Last Activity',
      render: (row) => (
        <div className="text-sm text-gray-500">
          {row.last_event ? (
            <>
              <p>{new Date(row.last_event).toLocaleDateString()}</p>
              <p className="text-xs">{new Date(row.last_event).toLocaleTimeString()}</p>
            </>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      )
    },
    {
      key: 'links',
      label: 'Links',
      render: (row) => (
        <div className="flex flex-col gap-1">
          {(row.render_url || row.custom_domain_url) && (
            <a
              href={row.custom_domain_url || row.render_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-900 text-xs"
            >
              🌐 Visit Site
            </a>
          )}
          <Link
            to={`/websites/${row.website_id}`}
            className="text-primary-600 hover:text-primary-900 text-xs"
          >
            📋 Details
          </Link>
        </div>
      )
    }
  ];

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Website Analytics</h1>
              <p className="text-gray-600 mt-1">
                View aggregated analytics for all deployed websites
              </p>
            </div>
            <button
              onClick={fetchWebsitesAnalytics}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {totals && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg mr-3">
                  <EyeIcon className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Events</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(totals.total_events)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <EyeIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Page Views</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(totals.total_page_views)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <CursorArrowRaysIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Clicks</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(totals.total_clicks)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <DocumentTextIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Form Submissions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(totals.total_form_submissions)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                  <UsersIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unique Visitors</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(totals.total_unique_visitors)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by website name, owner name, or email..."
                className="input-field pl-10 pr-10 w-full"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Date Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="input-field w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="input-field w-full"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors w-full"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {websites.length} of {totalWebsites} websites with analytics
        </div>

        {/* Websites Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                data={websites}
                loading={loading}
                emptyMessage="No websites with analytics data found"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default WebsiteAnalytics;
